import React, { Component } from 'react';
import { useParams } from 'react-router-dom';
import { HeaderModern, TopBar, TopHeader, Footer, Loader } from '../layouts/general';
import { API_BASE, getImageUrl } from 'config/api';
import BrochureDownloadLinks from '../layouts/blog/BrochureDownloadLinks';
import { fetchBrochureUrls } from '../layouts/blog/brochureLinks';
import SEO, { buildAbsoluteUrl, truncateDescription } from '../seo/SEO';

const defaultImage = (typeof process.env.PUBLIC_URL === 'string' ? process.env.PUBLIC_URL : '') + '/images/logo-blog.png';

const BlogDetailWrapper = (props) => {
    const params = useParams();
    return <BlogDetail {...props} params={params} />;
};

class BlogDetail extends Component {
    state = {
        headers: [{ id: 1, names: 'Resources' }],
        blog: null,
        loading: true,
        brochureUrls: null
    };

    componentDidMount() {
        this.fetchBlog();
        fetchBrochureUrls().then((brochureUrls) => this.setState({ brochureUrls }));
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params?.slug !== this.props.params?.slug) {
            this.setState({ loading: true, blog: null });
            this.fetchBlog();
        }
    }

    fetchBlog = async () => {
        const { slug } = this.props.params || {};
        if (!slug) {
            this.setState({ loading: false });
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/blog/${slug}`);
            const detail = res.ok ? await res.json() : null;
            this.setState({ blog: detail, loading: false });
        } catch (e) {
            this.setState({ blog: null, loading: false });
        }
    };

    renderShareLinks() {
        if (typeof window === 'undefined') return null;
        const url = encodeURIComponent(window.location.href);

        return (
            <div className="modern-resource-detail-share">
                <span>Share</span>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                    <i className="fa fa-facebook" aria-hidden="true" />
                </a>
                <a href={`https://twitter.com/intent/tweet?url=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
                    <i className="fa fa-twitter" aria-hidden="true" />
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
                    <i className="fa fa-linkedin" aria-hidden="true" />
                </a>
            </div>
        );
    }

    renderContent(blog) {
        const imageUrl = getImageUrl(blog.image);
        const tags = Array.isArray(blog.tags) ? blog.tags.filter(Boolean) : [];
        const { brochureUrls } = this.state;

        return (
            <>
                <section className="modern-resource-hero">
                    <div className="container">
                        <div className="modern-resource-hero-content">
                            {blog.category && (
                                <span className="modern-resource-hero-category">{blog.category}</span>
                            )}
                            <h1>{blog.title}</h1>
                            {blog.excerpt && <p>{blog.excerpt}</p>}
                            <div className="modern-resource-hero-meta">
                                {blog.date && (
                                    <span>
                                        <i className="fa fa-calendar" aria-hidden="true" />
                                        {blog.date}
                                    </span>
                                )}
                                {blog.author && (
                                    <span>
                                        <i className="fa fa-user" aria-hidden="true" />
                                        {blog.author}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="modern-resource-detail-page">
                    <div className="container">
                        <article className="modern-resource-detail-article">
                            <div className={`modern-resource-detail-grid${imageUrl ? '' : ' is-single-column'}`}>
                                {imageUrl && (
                                    <aside className="modern-resource-detail-media-col">
                                        <div className="modern-resource-detail-media">
                                            <img
                                                src={imageUrl}
                                                alt={blog.title}
                                                onError={(e) => { e.target.src = defaultImage; }}
                                            />
                                        </div>
                                    </aside>
                                )}

                                <div className="modern-resource-detail-main">
                                    <div
                                        className="modern-resource-article-content"
                                        dangerouslySetInnerHTML={{ __html: blog.content || '' }}
                                    />

                                    {(tags.length > 0 || blog.category || brochureUrls) && (
                                        <div className="modern-resource-detail-tags">
                                            {(tags.length > 0 || blog.category) && (
                                                <>
                                                    <span>Tags</span>
                                                    <div>
                                                        {blog.category && <span className="modern-resource-tag">{blog.category}</span>}
                                                        {tags.map((tag) => (
                                                            <span className="modern-resource-tag" key={tag}>{tag}</span>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {brochureUrls && (
                                                <BrochureDownloadLinks
                                                    pdfUrl={brochureUrls.pdf}
                                                    docUrl={brochureUrls.doc}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {this.renderShareLinks()}
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </>
        );
    }

    render() {
        const { blog, loading } = this.state;

        if (loading) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <Loader />
                        <TopBar />
                        <TopHeader />
                        <section className="modern-resource-detail-page">
                            <div className="container">
                                <p className="modern-resources-status">Loading article…</p>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        if (!blog) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <TopBar />
                        <TopHeader />
                        <section className="modern-resource-detail-page">
                            <div className="container">
                                <p className="modern-resources-status">Article not found.</p>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-body3">
                <div className="boxed">
                    <SEO
                        title={blog.metaTitle || `${blog.title} | Stats Elite Resources`}
                        description={blog.metaDescription || blog.excerpt || blog.content}
                        path={`/blog/${blog.slug || this.props.params?.slug || ''}`}
                        image={getImageUrl(blog.image) || undefined}
                        type="article"
                        schema={{
                            '@context': 'https://schema.org',
                            '@type': 'Article',
                            headline: blog.title,
                            description: truncateDescription(blog.excerpt || blog.content || ''),
                            image: getImageUrl(blog.image) || undefined,
                            author: blog.author ? { '@type': 'Person', name: blog.author } : { '@type': 'Organization', name: 'Stats Elite' },
                            publisher: { '@type': 'Organization', name: 'Stats Elite', logo: { '@type': 'ImageObject', url: buildAbsoluteUrl('/images/logo@2x.png') } },
                            mainEntityOfPage: buildAbsoluteUrl(`/blog/${blog.slug || this.props.params?.slug || ''}`),
                            datePublished: blog.date || undefined
                        }}
                    />
                    <Loader />
                    <TopBar />
                    <TopHeader />
                    {this.state.headers.map((data) => (
                        <HeaderModern data={{ ...data, names: blog.title }} key={data.id} />
                    ))}

                    {this.renderContent(blog)}

                    <Footer />
                </div>
            </div>
        );
    }
}

export default BlogDetailWrapper;
